import { jwtDecode } from 'jwt-decode';
import { useEffect } from 'react';

import { SHARED_CACHE_KEY } from '@sbrc/hooks/useFeatureFlag';
import { clearMustChangePin, logout } from '@sbrc/services';

/**
 * The restore command whose session has already been thrown away.
 *
 * In localStorage rather than in React state on purpose: this guards a full
 * page reload, so it has to survive one. The restore is still unacknowledged
 * when the page comes back, so a guard that reset on load would reload
 * again, and again, and the operator would never reach the login screen.
 */
export const RESTORE_SESSION_DROPPED_KEY = 'restore.sessionDropped';

/** Whether a landed restore still needs the browser session thrown away. */
export function shouldDropSession(
  state: string | undefined,
  commandId: string | null | undefined,
  alreadyDroppedFor: string | null,
): boolean {
  // Only a restore that actually applied. A failed one replaced nothing, so
  // the session still describes something real and there is nothing to drop.
  if (state !== 'succeeded' || !commandId) {
    return false;
  }

  return alreadyDroppedFor !== commandId;
}

/**
 * Whether the token in the browser was issued before the restore finished.
 *
 * The drop exists for a session the restore invalidated. Once feathers-bot
 * refuses the old token the operator signs back in, and what they get is a
 * session already authenticated against the restored database — dropping
 * that one too would charge them a second passcode entry to reach a
 * completion modal they could have been shown on the first.
 *
 * Anything unreadable counts as older. An unnecessary reload costs one
 * passcode entry; a skipped one leaves the operator holding a dead session,
 * which is the whole failure this guards against.
 */
export function isSessionOlderThanRestore(
  token: string | null,
  finishedAt: string | undefined,
): boolean {
  if (!token || !finishedAt) {
    return true;
  }

  const finished = Date.parse(finishedAt);

  if (Number.isNaN(finished)) {
    return true;
  }

  let issuedAt: number | undefined;

  try {
    ({ iat: issuedAt } = jwtDecode<{ iat?: number }>(token));
  } catch {
    return true;
  }

  if (typeof issuedAt !== 'number') {
    return true;
  }

  return issuedAt * 1000 < finished;
}

/**
 * Throws away what the page is carrying that describes the replaced database.
 *
 * The token is the load-bearing one and `logout()` handles it, but it is not
 * the only thing: the feature-flag cache was read out of the database that
 * has just been overwritten, and `mustChangePin` was captured at a login that
 * no longer refers to an account that exists.
 *
 * Two things are deliberately left alone. The reload guard has to survive the
 * reload it guards, or the page loops. And `featureFlag:<key>` overrides are
 * set by hand in the control panel — they belong to the person at the
 * browser, not to the robot, and clearing them would silently undo their
 * toggles for a reason that has nothing to do with them.
 *
 * Best effort throughout: a browser that refuses to write storage must not
 * take the reload down with it.
 */
export function clearRestoredStorage(): void {
  try {
    window.localStorage.removeItem(SHARED_CACHE_KEY);
  } catch {
    // Nothing to do: a cache that cannot be cleared is refreshed from the
    // box on the next load anyway.
  }

  try {
    clearMustChangePin();
  } catch {
    // Same. The flag is re-captured on the next successful login.
  }
}

/**
 * Ends the browser session once a restore has applied.
 *
 * The restore replaced the database this session authenticated against,
 * accounts and passcode included, so the token in localStorage now
 * authenticates against nothing. Leaving it there is what made the robot
 * look broken: the app keeps reconnecting with a token the box will never
 * accept again.
 *
 * Dropping it and reloading is deliberately blunt. There is nothing left in
 * the page worth preserving — every service behind it has just been
 * restarted under a different database — so the operator gets the login
 * screen, and the restore's own completion modal comes back after they log
 * in, because the command is still unacknowledged on disk.
 *
 * A session younger than the restore is left alone, and only marked as
 * handled. feathers-bot already refuses the old token, so by the time the
 * operator is signed in again their session is authenticated against the
 * restored database; reloading it would cost a second passcode entry and
 * land them exactly where they already are.
 */
export function useDropSessionAfterRestore(
  state: string | undefined,
  commandId: string | null | undefined,
  finishedAt?: string,
) {
  useEffect(() => {
    let alreadyDroppedFor: string | null = null;

    try {
      alreadyDroppedFor = window.localStorage.getItem(
        RESTORE_SESSION_DROPPED_KEY,
      );
    } catch {
      // An unreadable store cannot be written either, so the guard below
      // would not hold. Fall through to the write, which fails closed.
    }

    if (!shouldDropSession(state, commandId, alreadyDroppedFor) || !commandId) {
      return;
    }

    try {
      window.localStorage.setItem(RESTORE_SESSION_DROPPED_KEY, commandId);
    } catch {
      // The marker is the only thing standing between this and a reload
      // loop. If it cannot be recorded, leave the session alone: a stale
      // token the operator can clear is recoverable, a page that reloads
      // forever is not.
      return;
    }

    // Marked as handled above, so this one is settled either way. A session
    // that postdates the restore needs nothing thrown away.
    if (
      !isSessionOlderThanRestore(
        window.localStorage.getItem('feathers-jwt'),
        finishedAt,
      )
    ) {
      return;
    }

    // `logout` clears the token up front and never rejects, so the reload
    // can follow it immediately. It does its work *synchronously* though —
    // a browser refusing the storage write throws straight out of the call
    // rather than rejecting, which `.catch` would never see. Letting that
    // escape would crash the effect and strand the operator on the very
    // session this exists to end, so the reload goes ahead regardless.
    try {
      logout().catch(() => {});
    } catch {
      // Nothing to salvage: the token could not be cleared, and reloading
      // is still strictly better than staying on a dead page.
    }

    clearRestoredStorage();

    // Cache-busting navigation rather than `reload()`, following
    // `useSoftwareBuildData`: a new URL is fetched rather than revalidated,
    // and it replaces the history entry instead of stacking one the back
    // button could return the operator to a page whose session is gone.
    const url = new URL(window.location.href);

    url.searchParams.set('_cb', String(Date.now()));
    window.location.replace(url.toString());
  }, [state, commandId, finishedAt]);
}
